import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { formatDateForApi, parseDateFromApi } from '../core/utils/date.utils';

// Interfaces para el estado
export interface LiquidacionesFilter {
    clienteId?: number | null;
    temporada?: number | null;
    fechaInicio?: Date | null;
    fechaFin?: Date | null;
    filtroTexto?: string;
}

export interface LiquidacionesPagination {
    page: number;
    pageSize: number;
}

export interface LiquidacionesState {
    filters: LiquidacionesFilter;
    pagination: LiquidacionesPagination;
    lastUpdated: number;
}

export interface LiquidacionesVdState extends LiquidacionesState {
    filtroTipoEnvio?: string;
    filtroCoberturaSeguro?: string;
}

@Injectable({
    providedIn: 'root'
})
export class LiquidacionesStateService {
    private readonly VD_STATE_KEY = 'liquidaciones_vd_state';
    private readonly AGENCIA_STATE_KEY = 'liquidaciones_agencia_state';
    private readonly STATE_EXPIRY_HOURS = 24; // Horas antes de que expire el estado

    // Estados por defecto
    private readonly defaultVdState: LiquidacionesVdState = {
        filters: {
            clienteId: null,
            temporada: null,
            fechaInicio: null,
            fechaFin: null,
            filtroTexto: ''
        },
        pagination: { page: 1, pageSize: 10 },
        filtroTipoEnvio: '',
        filtroCoberturaSeguro: '',
        lastUpdated: Date.now()
    };

    private readonly defaultAgenciaState: LiquidacionesState = {
        filters: {
            clienteId: null,
            temporada: null,
            fechaInicio: null,
            fechaFin: null,
            filtroTexto: ''
        },
        pagination: { page: 1, pageSize: 10 },
        lastUpdated: Date.now()
    };

    // BehaviorSubjects para estado reactivo
    private vdStateSubject = new BehaviorSubject<LiquidacionesVdState>(this.defaultVdState);
    private agenciaStateSubject = new BehaviorSubject<LiquidacionesState>(this.defaultAgenciaState);

    constructor() {
        this.initializeStates();
    }

    /**
     * Inicializar estados desde localStorage si están disponibles
     */
    private initializeStates(): void {
        const vdState = this.loadVdStateFromStorage();
        if (vdState && !this.isStateExpired(vdState.lastUpdated)) {
            this.vdStateSubject.next(vdState);
        }

        const agenciaState = this.loadAgenciaStateFromStorage();
        if (agenciaState && !this.isStateExpired(agenciaState.lastUpdated)) {
            this.agenciaStateSubject.next(agenciaState);
        }
    }

    /**
     * Verificar si un estado ha expirado
     */
    private isStateExpired(lastUpdated: number): boolean {
        const now = Date.now();
        const expiryTime = this.STATE_EXPIRY_HOURS * 60 * 60 * 1000; // Convertir a ms
        return (now - lastUpdated) > expiryTime;
    }

    // ====================================================
    // MÉTODOS PARA LIQUIDACIONES VD
    // ====================================================

    /**
     * Obtener estado observable de VD
     */
    getVdState(): Observable<LiquidacionesVdState> {
        return this.vdStateSubject.asObservable();
    }

    /**
     * Obtener estado actual de VD
     */
    getCurrentVdState(): LiquidacionesVdState {
        return this.vdStateSubject.value;
    }

    /**
     * Actualizar estado de VD
     */
    updateVdState(partialState: Partial<LiquidacionesVdState>): void {
        const currentState = this.vdStateSubject.value;
        const newState: LiquidacionesVdState = {
            ...currentState,
            ...partialState,
            lastUpdated: Date.now()
        };
        
        this.vdStateSubject.next(newState);
        this.saveVdStateToStorage(newState);
    }

    /**
     * Actualizar solo filtros de VD
     */
    updateVdFilters(filters: Partial<LiquidacionesFilter>): void {
        const currentState = this.getCurrentVdState();
        this.updateVdState({
            filters: { ...currentState.filters, ...filters },
            pagination: { ...currentState.pagination, page: 1 } // Reset a página 1
        });
    }

    /**
     * Actualizar solo paginación de VD
     */
    updateVdPagination(pagination: Partial<LiquidacionesPagination>): void {
        const currentState = this.getCurrentVdState();
        this.updateVdState({
            pagination: { ...currentState.pagination, ...pagination }
        });
    }

    /**
     * Limpiar estado de VD
     */
    clearVdState(): void {
        this.vdStateSubject.next(this.defaultVdState);
        this.clearVdStateFromStorage();
    }

    // ====================================================
    // MÉTODOS PARA LIQUIDACIONES AGENCIA
    // ====================================================

    /**
     * Obtener estado observable de Agencia
     */
    getAgenciaState(): Observable<LiquidacionesState> {
        return this.agenciaStateSubject.asObservable();
    }

    /**
     * Obtener estado actual de Agencia
     */
    getCurrentAgenciaState(): LiquidacionesState {
        return this.agenciaStateSubject.value;
    }

    /**
     * Actualizar estado de Agencia
     */
    updateAgenciaState(partialState: Partial<LiquidacionesState>): void {
        const currentState = this.agenciaStateSubject.value;
        const newState: LiquidacionesState = {
            ...currentState,
            ...partialState,
            lastUpdated: Date.now()
        };
        
        this.agenciaStateSubject.next(newState);
        this.saveAgenciaStateToStorage(newState);
    }

    /**
     * Actualizar solo filtros de Agencia
     */
    updateAgenciaFilters(filters: Partial<LiquidacionesFilter>): void {
        const currentState = this.getCurrentAgenciaState();
        this.updateAgenciaState({
            filters: { ...currentState.filters, ...filters },
            pagination: { ...currentState.pagination, page: 1 } // Reset a página 1
        });
    }

    /**
     * Actualizar solo paginación de Agencia
     */
    updateAgenciaPagination(pagination: Partial<LiquidacionesPagination>): void {
        const currentState = this.getCurrentAgenciaState();
        this.updateAgenciaState({
            pagination: { ...currentState.pagination, ...pagination }
        });
    }

    /**
     * Limpiar estado de Agencia
     */
    clearAgenciaState(): void {
        this.agenciaStateSubject.next(this.defaultAgenciaState);
        this.clearAgenciaStateFromStorage();
    }

    // ====================================================
    // MÉTODOS DE LOCALSTORAGE
    // ====================================================

    /**
     * Guardar estado de VD en localStorage
     */
    private saveVdStateToStorage(state: LiquidacionesVdState): void {
        try {
            localStorage.setItem(this.VD_STATE_KEY, JSON.stringify(state));
        } catch (error) {
            console.warn('Error saving VD state to localStorage:', error);
        }
    }

    /**
     * Cargar estado de VD desde localStorage
     */
    private loadVdStateFromStorage(): LiquidacionesVdState | null {
        try {
            const saved = localStorage.getItem(this.VD_STATE_KEY);
            if (saved) {
                const state = JSON.parse(saved);
                // Convertir fechas de string a Date si existen
                if (state.filters.fechaInicio) {
                    state.filters.fechaInicio = new Date(state.filters.fechaInicio);
                }
                if (state.filters.fechaFin) {
                    state.filters.fechaFin = new Date(state.filters.fechaFin);
                }
                return state;
            }
        } catch (error) {
            console.warn('Error loading VD state from localStorage:', error);
        }
        return null;
    }

    /**
     * Limpiar estado de VD de localStorage
     */
    private clearVdStateFromStorage(): void {
        try {
            localStorage.removeItem(this.VD_STATE_KEY);
        } catch (error) {
            console.warn('Error clearing VD state from localStorage:', error);
        }
    }

    /**
     * Guardar estado de Agencia en localStorage
     */
    private saveAgenciaStateToStorage(state: LiquidacionesState): void {
        try {
            localStorage.setItem(this.AGENCIA_STATE_KEY, JSON.stringify(state));
        } catch (error) {
            console.warn('Error saving Agencia state to localStorage:', error);
        }
    }

    /**
     * Cargar estado de Agencia desde localStorage
     */
    private loadAgenciaStateFromStorage(): LiquidacionesState | null {
        try {
            const saved = localStorage.getItem(this.AGENCIA_STATE_KEY);
            if (saved) {
                const state = JSON.parse(saved);
                // Convertir fechas de string a Date si existen
                if (state.filters.fechaInicio) {
                    state.filters.fechaInicio = new Date(state.filters.fechaInicio);
                }
                if (state.filters.fechaFin) {
                    state.filters.fechaFin = new Date(state.filters.fechaFin);
                }
                return state;
            }
        } catch (error) {
            console.warn('Error loading Agencia state from localStorage:', error);
        }
        return null;
    }

    /**
     * Limpiar estado de Agencia de localStorage
     */
    private clearAgenciaStateFromStorage(): void {
        try {
            localStorage.removeItem(this.AGENCIA_STATE_KEY);
        } catch (error) {
            console.warn('Error clearing Agencia state from localStorage:', error);
        }
    }

    // ====================================================
    // MÉTODOS DE UTILIDAD
    // ====================================================

    /**
     * Limpiar todos los estados expirados
     */
    cleanupExpiredStates(): void {
        const vdState = this.loadVdStateFromStorage();
        if (vdState && this.isStateExpired(vdState.lastUpdated)) {
            this.clearVdStateFromStorage();
        }

        const agenciaState = this.loadAgenciaStateFromStorage();
        if (agenciaState && this.isStateExpired(agenciaState.lastUpdated)) {
            this.clearAgenciaStateFromStorage();
        }
    }

    /**
     * Convertir estado a query parameters para la URL
     */
    stateToQueryParams(state: LiquidacionesState | LiquidacionesVdState): any {
        const params: any = {};

        // Filtros
        if (state.filters.clienteId) params.clienteId = state.filters.clienteId.toString();
        if (state.filters.temporada) params.temporada = state.filters.temporada.toString();
        if (state.filters.fechaInicio) params.fechaInicio = formatDateForApi(state.filters.fechaInicio);
        if (state.filters.fechaFin) params.fechaFin = formatDateForApi(state.filters.fechaFin);
        if (state.filters.filtroTexto) params.filtroTexto = state.filters.filtroTexto;

        // Paginación
        if (state.pagination.page !== 1) params.page = state.pagination.page.toString();
        if (state.pagination.pageSize !== 10) params.pageSize = state.pagination.pageSize.toString();

        // Filtros específicos de VD
        if ('filtroTipoEnvio' in state && state.filtroTipoEnvio) {
            params.filtroTipoEnvio = state.filtroTipoEnvio;
        }
        if ('filtroCoberturaSeguro' in state && state.filtroCoberturaSeguro) {
            params.filtroCoberturaSeguro = state.filtroCoberturaSeguro;
        }

        return params;
    }

    /**
     * Convertir query parameters a estado
     */
    queryParamsToState(params: any, isVd: boolean = false): LiquidacionesState | LiquidacionesVdState {
        const baseState: LiquidacionesState = {
            filters: {
                clienteId: params.clienteId ? +params.clienteId : null,
                temporada: params.temporada ? +params.temporada : null,
                fechaInicio: params.fechaInicio ? parseDateFromApi(params.fechaInicio) : null,
                fechaFin: params.fechaFin ? parseDateFromApi(params.fechaFin) : null,
                filtroTexto: params.filtroTexto || ''
            },
            pagination: {
                page: params.page ? +params.page : 1,
                pageSize: params.pageSize ? +params.pageSize : 10
            },
            lastUpdated: Date.now()
        };

        if (isVd) {
            return {
                ...baseState,
                filtroTipoEnvio: params.filtroTipoEnvio || '',
                filtroCoberturaSeguro: params.filtroCoberturaSeguro || ''
            } as LiquidacionesVdState;
        }

        return baseState;
    }
}